'use client';

import { useEffect, useRef, useState } from 'react';

/** How far outside the viewport a deferred item starts to render. */
const REVEAL_MARGIN = '600px';

/**
 * Heights that deferred items had once they rendered, by item and window
 * width. When a list mounts again ("Back to lot listing") its placeholders
 * take these heights, so the page above each lot is as tall as it was and the
 * saved scroll position lands on the same lot. Module state: it lasts while
 * the page does, and a reload starts it empty.
 */
const renderedHeights = new Map<string, number>();

function heightKey(cacheKey: string) {
  return `${window.innerWidth}|${cacheKey}`;
}

/** Forgets every remembered height. For tests. */
export function clearDeferredHeights() {
  renderedHeights.clear();
}

/**
 * Defers an item's content until it comes within REVEAL_MARGIN of the
 * viewport, so a long list mounts only what is near the screen. Once shown,
 * the item's height is remembered under `cacheKey` (when given) for the next
 * time the list mounts. Returns the ref for the item's box, whether its
 * content is shown, and the placeholder height to keep until then.
 */
export function useDeferredReveal(
  cacheKey: string | undefined,
  fallbackHeight: number,
) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  // Read once: a placeholder keeps the height it mounted with.
  const [placeholderHeight] = useState(
    () =>
      (cacheKey !== undefined && typeof window !== 'undefined'
        ? renderedHeights.get(heightKey(cacheKey))
        : undefined) ?? fallbackHeight,
  );

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Reveal once after hydration when this browser cannot observe visibility.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: REVEAL_MARGIN },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = ref.current;
    if (!visible || cacheKey === undefined || !container) return;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (container.offsetHeight > 0) {
        renderedHeights.set(heightKey(cacheKey), container.offsetHeight);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [visible, cacheKey]);

  return { ref, visible, placeholderHeight };
}
